import type { Middleware, UnknownAction } from 'redux';
import commonActions from '../Actions/commonActions';
import { apiActionTypes } from '../ActionTypes/apiActionTypes';
import type { ApiErrorAction } from '../Actions/apiActions';
import type { ApiError } from '../Utils/httpClient';
import urlUtils from '../Utils/urlUtils';
import type { AppDispatch } from '../Store/types';

function isApiErrorAction(action: unknown): action is ApiErrorAction {
    return typeof action === 'object' && action !== null && (action as UnknownAction).type === apiActionTypes.API_ERROR;
}

function handleApiError(dispatch: AppDispatch, error: ApiError): void {
    if (error.errorType !== undefined) {
        if (error.errorMessage !== undefined && error.errorMessage !== '') {
            // API sends Title as "" when unset; treat empty as absent so showError gets a heading.
            const title = error.title !== undefined && error.title !== '' ? error.title : 'Business error';
            if (error.errorType === 'BusinessWarningException') {
                dispatch(commonActions.showWarning(error.errorMessage, title, -1));
            } else if (error.errorType === 'VonUnauthorizedException') {
                dispatch(commonActions.isSignedInToVon(false));
            } else {
                dispatch(commonActions.showError(error.errorMessage, title));
            }
        }
        if (error.redirectTo !== undefined && error.redirectTo !== '') {
            const safePath = urlUtils.toSameOriginPath(error.redirectTo);
            if (safePath !== null) {
                window.location.href = safePath;
            } else {
                console.error(`Refused an unsafe API redirect target: ${error.redirectTo}`);
            }
        }
        return;
    }

    if (error.kind === 'network' || error.status === 401) {
        dispatch(commonActions.reset());
    } else if (error.status === 403) {
        dispatch(commonActions.showError('You are not allowed to proceed. Please contact Business Automation due Selfservice-portal.', 'Access denied'));
    } else {
        // Use error.message, not statusText: HTTP/2 has no reason phrase (blank on App Service).
        dispatch(commonActions.showError(error.message));
    }
}

export const apiErrorMiddleware: Middleware = (store) => (next) => (action: unknown) => {
    if (isApiErrorAction(action)) {
        handleApiError(store.dispatch as AppDispatch, action.error);
        return;
    }
    return next(action);
};

export default apiErrorMiddleware;
